import {
  createRootRouteWithContext,
  createRoute,
  redirect,
} from '@tanstack/react-router';
import {
  getGetProfileQueryKey,
  getGetProfileQueryOptions,
} from '@biasafe.queries/user/user';
import type { QueryClient } from '@tanstack/react-query';
import { AppLayout } from '@/app/layouts/AppLayout';
import { AuthLayout } from '@/app/layouts/AuthLayout';
import { useUserStore } from '@/entities/user/store';
import { clearAccess } from '@/features/Auth/accessState';
import { ensureAccess } from '@/features/Auth/accessSession';
import { canonicalPortalLocation } from '@/features/Auth/portalLocation';
import { config } from '@/shared/config/env';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';
import { fetchMfaStatus, mfaIsDeferred } from '@/features/Auth/gcp/emailMfa';
import {
  clearSignInStamp,
  loginRedirectSearch,
} from '@/features/Auth/sessionNotices';

export interface RootRouteContext {
  queryClient: QueryClient;
}

/**
 * Send the caller to two-factor setup unless the BACKEND says they have one.
 *
 * Two-factor is MANDATORY here (FINRA/SEC treat it as a control on systems
 * handling customer accounts, not a preference), and a mandatory control has to
 * hold on every door into the app, not just the main one.
 *
 * IT DID NOT. This check lived inline in `authRoute` only, and `/onboarding` is
 * a sibling of `authRoute` rather than a child of it — it renders without the
 * sidebar chrome, so it was hung off the root. Its own guard read
 * `useUserStore.getState().isAuthenticated`, a client-side flag, and nothing
 * else. So the gate never ran there, and the signup flow walks straight into
 * it: confirming an address sets `biasafe_onboarding_pending`, `postLoginPath()`
 * then returns '/onboarding', and the whole workspace-creation flow completed
 * with no second factor enrolled. The gate was only met later, on the first
 * visit to a route that happened to sit under `authRoute`.
 *
 * Signing out at the gate and coming back in reproduced it from the other
 * direction, which is how it was reported.
 *
 * Extracted rather than copied so the next route added outside `authRoute`
 * has one obvious thing to call, and so "which doors are guarded" is a question
 * with one answer instead of one per route.
 *
 * Authoritative on purpose: the backend reads Identity Platform and the
 * Firebase custom claim. Nothing the browser holds can sway it — a client that
 * could would be opting itself out of a regulatory control, which is exactly
 * what the `isAuthenticated` flag on its own allowed.
 *
 * DEPLOYMENT PREREQUISITE, and it is not in this repo. This gate is only as
 * usable as the factors the Identity Platform project actually offers. Read the
 * live config with:
 *
 *   GET https://identitytoolkit.googleapis.com/admin/v2/projects/<id>/config
 *
 * On biasafe-dev-app-mj as of this commit it answers `mfa: {state: DISABLED}`
 * and no phoneNumber provider, so BOTH Firebase-backed methods fail the moment
 * they are chosen -- `auth/operation-not-allowed`, surfaced as "This method is
 * not enabled for BIASafe yet". Every account is sent here and only the email
 * factor can be completed, because that one is ours (a code we send, a custom
 * claim we set) and does not touch Firebase MFA.
 *
 * Read 2026-10-03: staging and prod have `mfa.state: ENABLED` with
 * `enabledProviders: [PHONE_SMS]` and NO TOTP provider config, so the
 * authenticator app (the method shown first, as Recommended) fails there with
 * `auth/operation-not-allowed` until TOTP is enabled; SMS is limited to regions
 * US and CA. Dev has MFA disabled outright.
 *
 * Which means the gate degrades to a single method rather than dead-ending --
 * UNLESS email delivery is also down, and then it dead-ends completely. A
 * mandatory gate with nothing behind it locks every user out of the product.
 * So enabling MFA (with TOTP) on each environment's project is a release
 * prerequisite, not a follow-up.
 *
 * Callers must invoke this OUTSIDE any try block that catches. TanStack signals
 * a redirect by throwing, so a catch would swallow it and turn "go set up your
 * second factor" into whatever that catch does instead — in authRoute's case, a
 * logout.
 */
export async function requireSecondFactor(
  queryClient: QueryClient,
): Promise<void> {
  const status = await queryClient.ensureQueryData({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
  });
  // MFA_ENFORCEMENT_MODE=before_regulated_action: the backend says an
  // unenrolled user may reach onboarding and the dashboard, and refuses them
  // (403 AU403MFAA) at the first regulated action instead, where the API
  // client sends them to /security-setup with a way back. The decision is the
  // BACKEND's, in the same authoritative response as `enrolled`, so nothing the
  // browser holds can switch it on. Any other value -- including a missing one
  // from an older API -- keeps the gate strict, and the gate above is what the
  // past bypass bug (onboarding reachable with no second factor) was about, so
  // it stays in force in the default mode.
  if (!status.enrolled && !mfaIsDeferred(status)) {
    throw redirect({ to: '/security-setup' });
  }
}

export const rootRoute = createRootRouteWithContext<RootRouteContext>()({
  beforeLoad: ({ location }) => {
    const canonical = canonicalPortalLocation(
      new URL(location.href, window.location.origin),
      config.app.clientPortalUrl,
    );
    if (canonical) throw redirect({ href: canonical, replace: true });
  },
  component: AppLayout,
});

/**
 * Whether the API refused this caller for being in the wrong product.
 *
 * Narrow on the CODE, not the status. A 403 from the profile route could in
 * principle mean something else one day, and treating every one of them as
 * "you are a client" would bounce a colleague into a portal that will refuse
 * them — a loop with no way out.
 */
function isWrongAudience(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return false;
  }
  const response = (
    err as {
      response?: { status?: number; data?: { detail?: { code?: unknown } } };
    }
  ).response;
  return response?.status === 403 && response.data?.detail?.code === 'AU403WA';
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  beforeLoad: async ({ context: { queryClient } }) => {
    if (!useUserStore.getState().isAuthenticated)
      throw redirect({ to: '/login' });
    let hasAccess;
    try {
      hasAccess = await ensureAccess();
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if (status !== 401 && status !== 403) throw error;
      useUserStore.getState().logout();
      throw redirect({ to: '/login', search: loginRedirectSearch() });
    }
    if (!hasAccess) throw redirect({ to: '/access', replace: true });

    let profile;
    try {
      profile = await queryClient.ensureQueryData(
        getGetProfileQueryOptions({
          query: {
            queryKey: getGetProfileQueryKey(),
            retry: false,
            staleTime: PROFILE_STALE_TIME,
          },
        }),
      );
    } catch (err) {
      if (isWrongAudience(err)) {
        clearAccess('advisor');
        throw redirect({ to: '/access', replace: true });
      }
      const status = (err as { response?: { status?: number } }).response
        ?.status;
      // A temporary outage never changes the person's session or membership.
      if (status !== 401 && status !== 403) throw err;
      useUserStore.getState().logout();
      throw redirect({ to: '/login', search: loginRedirectSearch() });
    }
    // The API accepted the session, so a later bounce is an ordinary expiry.
    clearSignInStamp();

    // The gate that already proves the session also proves the second factor,
    // so no protected data loads for an unprotected account. See
    // requireSecondFactor for why it is authoritative and why this call sits
    // OUTSIDE the try above (a redirect is a throw; that catch would turn it
    // into a logout).
    await requireSecondFactor(queryClient);

    // Published on the route context so the layout and its children read a
    // profile that is known-good rather than re-deriving one.
    return { profile };
  },
  component: AuthLayout,
});
